import * as Cesium from 'cesium';
import type { QualityMode } from '../config/appConfig';

interface FacadeShaderState {
  shader: Cesium.CustomShader;
}

const facadeShaders = new WeakMap<Cesium.Cesium3DTileset, FacadeShaderState>();
const ROOF_IMAGERY_URL = '/data/roof-imagery/tokushima-center-z16.jpg';
const ROOF_PROJECTION = new Cesium.Cartesian4(57258.0, 26160.0, 16.0, 8.0);

const QUALITY_UNIFORMS: Record<QualityMode, {
  facadeStrength: number;
  windowStrength: number;
  roofStrength: number;
}> = {
  eco: { facadeStrength: 0.26, windowStrength: 0.52, roofStrength: 1.0 },
  standard: { facadeStrength: 0.35, windowStrength: 0.64, roofStrength: 1.0 },
  high: { facadeStrength: 0.44, windowStrength: 0.76, roofStrength: 1.0 },
};

const FACADE_FRAGMENT_SHADER = `
float buildingFacadeHash(vec3 value) {
  vec3 cell = floor(value * 0.03);
  return fract(sin(dot(cell, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
}

void fragmentMain(FragmentInput fsInput, inout czm_modelMaterial material) {
  vec3 position = fsInput.attributes.positionMC;
  vec3 normalEC = normalize(fsInput.attributes.normalEC);
  vec3 positionWC = fsInput.attributes.positionWC;
  vec3 normalWC = normalize(czm_inverseViewRotation3D * normalEC);

  // ECEFの半径方向は地心緯度になるため、屋根判定には使わない。
  // WGS84楕円体の測地面法線を使うことで、徳島付近でも建物の水平面を
  // 正しく屋根として扱う。
  vec3 ellipsoidRadii = czm_ellipsoidRadii;
  vec3 ellipsoidRadiiSquared = ellipsoidRadii * ellipsoidRadii;
  vec3 worldUpWC = normalize(positionWC / ellipsoidRadiiSquared);

  // 建物の原点を基準にしたローカルなワールド座標を使うことで、
  // ECEFの大きな数値による窓模様のちらつきを避ける。
  vec3 positionWorldRelative =
    (czm_model * vec4(position, 1.0)).xyz - czm_model[3].xyz;

  // ECEF位置からその地点の地表面法線を求め、垂直な壁面だけへ模様を出す。
  // 視点座標のZ成分ではなくワールド座標で判定することで、俯瞰角度に
  // 依存せず、屋根へ窓模様が投影されるのを防ぐ。
  float verticalSurface = abs(dot(normalWC, worldUpWC));
  float wallMask = 1.0 - smoothstep(0.16, 0.60, verticalSurface);
  float roofMask = smoothstep(0.52, 0.88, verticalSurface);

  // 屋根面では地表面方向への射影がゼロになるため、壁面用の接線を
  // フォールバック軸から作る。これによりNaNによる黒い描画を防ぐ。
  vec3 facadeUpProjected = worldUpWC - normalWC * dot(worldUpWC, normalWC);
  float facadeUpLength = length(facadeUpProjected);
  vec3 fallbackAxis = abs(normalWC.z) < 0.9
    ? vec3(0.0, 0.0, 1.0)
    : vec3(1.0, 0.0, 0.0);
  vec3 facadeUp = facadeUpLength > 0.001
    ? facadeUpProjected / facadeUpLength
    : normalize(cross(normalWC, fallbackAxis));
  vec3 facadeSide = normalize(cross(facadeUp, normalWC));

  // Web Mercatorのタイル座標へ変換し、徳島中心部の航空写真を屋根へ投影する。
  // 投影範囲外は元の建物マテリアルを維持する。
  const float PI = 3.14159265359;
  float longitude = atan(positionWC.y, positionWC.x);
  float equatorialDistance = length(positionWC.xy);
  float semiMajorAxis = ellipsoidRadii.x;
  float semiMinorAxis = ellipsoidRadii.z;
  float eccentricitySquared = 1.0
    - (semiMinorAxis * semiMinorAxis)
    / (semiMajorAxis * semiMajorAxis);
  float secondEccentricitySquared = (
    semiMajorAxis * semiMajorAxis
    - semiMinorAxis * semiMinorAxis
  ) / (semiMinorAxis * semiMinorAxis);
  float bowringTheta = atan(
    positionWC.z * semiMajorAxis,
    equatorialDistance * semiMinorAxis
  );
  float sinBowringTheta = sin(bowringTheta);
  float cosBowringTheta = cos(bowringTheta);
  float latitude = atan(
    positionWC.z + secondEccentricitySquared * semiMinorAxis
      * sinBowringTheta * sinBowringTheta * sinBowringTheta,
    equatorialDistance - eccentricitySquared * semiMajorAxis
      * cosBowringTheta * cosBowringTheta * cosBowringTheta
  );
  float sinLatitude = clamp(sin(latitude), -0.9999, 0.9999);
  float mapScale = exp2(u_roofProjection.z);
  vec2 mapPoint = vec2(
    (longitude + PI) / (2.0 * PI) * mapScale,
    (0.5 - log((1.0 + sinLatitude) / (1.0 - sinLatitude)) / (4.0 * PI)) * mapScale
  );
  vec2 roofUv = (mapPoint - u_roofProjection.xy) / u_roofProjection.w;
  float roofCoverage = step(0.0, roofUv.x)
    * step(0.0, roofUv.y)
    * step(roofUv.x, 1.0)
    * step(roofUv.y, 1.0);
  // GSIのXYZ画像は北側が画像上端。TextureUniformはWebGLの上下反転を
  // 適用して読み込むため、サンプル時のVだけを反転して地理座標と一致させる。
  vec2 roofTextureUv = vec2(roofUv.x, 1.0 - roofUv.y);
  float horizontal = dot(positionWorldRelative, facadeSide);
  float vertical = dot(positionWorldRelative, facadeUp);
  float columnCell = fract(horizontal / 4.0);
  float floorCell = fract(vertical / 3.0);

  float windowMask = step(0.22, columnCell)
    * (1.0 - step(0.78, columnCell))
    * step(0.23, floorCell)
    * (1.0 - step(0.77, floorCell))
    * wallMask;
  float floorLine = 1.0 - smoothstep(
    0.0,
    0.045,
    min(floorCell, 1.0 - floorCell)
  );
  float columnLine = 1.0 - smoothstep(
    0.0,
    0.035,
    min(columnCell, 1.0 - columnCell)
  );

  float buildingSeed = buildingFacadeHash(position);
  float facadeTone = mix(0.82, 1.08, buildingSeed);
  vec3 facadeColor = material.diffuse * facadeTone;
  facadeColor *= mix(
    vec3(0.95, 0.97, 1.0),
    vec3(1.0, 0.94, 0.84),
    buildingSeed * 0.28
  );
  facadeColor = mix(
    facadeColor,
    facadeColor * 0.70,
    clamp(floorLine * 0.34 + columnLine * 0.12, 0.0, 1.0) * wallMask
  );

  // 昼間は暗いガラス、夜間は建物ごとに点灯率を変えた窓明かりにする。
  float windowSeed = buildingFacadeHash(vec3(
    floor(horizontal / 4.0),
    floor(vertical / 3.0),
    floor(dot(positionWorldRelative, worldUpWC) * 0.02)
  ));
  float windowLit = step(0.53, windowSeed);
  vec3 windowDay = vec3(0.12, 0.17, 0.20);
  vec3 windowNight = mix(
    vec3(1.0, 0.40, 0.10),
    vec3(1.0, 0.78, 0.38),
    buildingFacadeHash(position + vec3(4.0, 8.0, 12.0))
  );
  vec3 nightWindowColor = mix(windowDay * 0.62, windowNight, windowLit);
  vec3 windowColor = mix(windowDay, nightWindowColor, u_nightFactor);

  material.diffuse = mix(
    material.diffuse,
    facadeColor,
    clamp(u_facadeStrength * wallMask, 0.0, 1.0)
  );
  material.diffuse = mix(
    material.diffuse,
    windowColor,
    clamp(windowMask * u_windowStrength, 0.0, 1.0)
  );
  if (roofMask * roofCoverage > 0.001) {
    vec3 roofImage = texture(u_roofTexture, roofTextureUv).rgb;
    material.diffuse = mix(
      material.diffuse,
      roofImage,
      roofMask * roofCoverage * u_roofStrength
    );
    material.roughness = mix(
      material.roughness,
      0.72,
      roofMask * roofCoverage * 0.55
    );
  }
  material.emissive += windowNight
    * windowMask
    * windowLit
    * u_nightFactor
    * 0.38;

  // 窓を少し滑らかにし、壁面はマットにする。PBR対応モデルでは日光の見え方にも反映される。
  material.roughness = mix(
    material.roughness,
    mix(0.78, 0.34, windowMask),
    wallMask * 0.72
  );
}
`;

function createFacadeShader(): Cesium.CustomShader {
  return new Cesium.CustomShader({
    mode: Cesium.CustomShaderMode.MODIFY_MATERIAL,
    uniforms: {
      u_facadeStrength: {
        type: Cesium.UniformType.FLOAT,
        value: QUALITY_UNIFORMS.eco.facadeStrength,
      },
      u_windowStrength: {
        type: Cesium.UniformType.FLOAT,
        value: QUALITY_UNIFORMS.eco.windowStrength,
      },
      u_roofStrength: {
        type: Cesium.UniformType.FLOAT,
        value: QUALITY_UNIFORMS.eco.roofStrength,
      },
      u_roofProjection: {
        type: Cesium.UniformType.VEC4,
        value: ROOF_PROJECTION,
      },
      u_roofTexture: {
        type: Cesium.UniformType.SAMPLER_2D,
        value: new Cesium.TextureUniform({
          url: ROOF_IMAGERY_URL,
          repeat: false,
          maximumAnisotropy: 4,
        }),
      },
      u_nightFactor: {
        type: Cesium.UniformType.FLOAT,
        value: 0.0,
      },
    },
    fragmentShaderText: FACADE_FRAGMENT_SHADER,
  });
}

export function applyBuildingFacadeShader(
  tileset: Cesium.Cesium3DTileset,
  quality: QualityMode,
): void {
  if (facadeShaders.has(tileset)) {
    setBuildingFacadeQuality(tileset, quality);
    return;
  }

  try {
    const shader = createFacadeShader();
    tileset.customShader = shader;
    facadeShaders.set(tileset, { shader });
    setBuildingFacadeQuality(tileset, quality);
    console.info('[Building facade] LOD1 procedural facade shader attached');
  } catch (error) {
    // シェーダー非対応端末では、元の建物マテリアルを維持して表示を継続する。
    console.warn('[Building facade] custom shader unavailable; original material kept', error);
  }
}

export function setBuildingFacadeQuality(
  tileset: Cesium.Cesium3DTileset,
  quality: QualityMode,
): void {
  const state = facadeShaders.get(tileset);
  if (!state) return;
  const uniforms = QUALITY_UNIFORMS[quality];
  state.shader.setUniform('u_facadeStrength', uniforms.facadeStrength);
  state.shader.setUniform('u_windowStrength', uniforms.windowStrength);
  state.shader.setUniform('u_roofStrength', uniforms.roofStrength);
}

export function setBuildingFacadeNightFactor(
  tileset: Cesium.Cesium3DTileset,
  nightFactor: number,
): void {
  const state = facadeShaders.get(tileset);
  if (!state) return;
  state.shader.setUniform('u_nightFactor', Cesium.Math.clamp(nightFactor, 0.0, 1.0));
}

export function nightFactorForDate(date: Date): number {
  const hour = date.getHours() + date.getMinutes() / 60;
  if (hour >= 20 || hour < 4) return 1.0;
  if (hour >= 18) return (hour - 18) / 2;
  if (hour < 6) return (6 - hour) / 2;
  return 0.0;
}
